import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';

import {
  BibleLanguagesGroup,
  CountDownText,
  LoadingSpinner,
  PngIcon,
  PromotionLearnMoreCard,
  SvgIcon,
} from '../../components/Molecules';
import { DEFAULT_APP_STORE_URL } from '../../constants/appInfo';
import { NOT_APPLICABLE_STRING } from '../../constants/general';
import { getGiftCardValue } from '../../constants/giftCard';
import { Icons, IconType } from '../../constants/icons';
import { Images } from '../../constants/images';
import { PNGIconName } from '../../constants/pngIcon';
import { SVGIconType } from '../../constants/svgIcon';
import { useShareData } from '../../context';
import useDeepLink from '../../hooks/useDeeplink';
import { TranslationLocale } from '../../i18n';
import { UiLocale } from '../../models/Base';
import { WebpageUI } from '../../models/UserStore/Invitation';
import { useUserStore } from '../../stores/UserStore';
import { getUiLocale } from '../../utils/i18n';

import styles from './PromotionRewardsScreen.module.css';

const { fetchRedemptionCode } = useUserStore.getState();

const OpenDetails = () => {
  const { t } = useTranslation();

  return (
    <div className={styles.learnMore}>
      {t('Learn more about JOYAJOY')}
      <a
        className={styles.url}
        href="https://joyajoy.com"
        target="_blank"
        rel="noreferrer"
      >
        www.JOYAJOY.com
      </a>
    </div>
  );
};

export const ShareUrl = () => {
  const [copied, setCopied] = useState(false);
  const { t } = useTranslation();

  const onCopyLink = () => {
    const pageUrl = window.location.href;

    if (navigator.share) {
      navigator.share({ url: pageUrl }).catch((err) => {
        console.error('Failed to share the URL: ', err);
      });
    } else {
      navigator.clipboard
        .writeText(pageUrl)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000); // reset after 2s
        })
        .catch((err) => {
          console.error('Failed to copy the URL: ', err);
        });
    }
  };

  return (
    <div className={styles.button} onClick={onCopyLink}>
      <p>
        {t(copied ? 'Link is copied!' : 'Copy Link')}
        {/* {Share this Gift Card before Expiry} */}
      </p>
      {/* {!copied && (
        <SvgIcon
          name={'Arrow'}
          className={styles.arrowIcon}
          width={20}
          height={20}
          color={'#ffffff'} // #00a698
        />
      )} */}
    </div>
  );
};

const LearnMoreCardGroup = ({
  type,
}: {
  type: WebpageUI | null | undefined;
}) => {
  const { t } = useTranslation();
  // const getBibleBannerImage = (lang: string) => {
  //   switch (lang) {
  //     case 'zh-CN':
  //       return Images.ArkWord_Church_Kickstarter_IntegratedBanner_SC;
  //     case 'zh-HK':
  //     case 'zh-TW':
  //       return Images.ArkWord_Church_Kickstarter_IntegratedBanner_TC;
  //     case 'ja-JP':
  //       return Images.ArkWord_Church_Kickstarter_IntegratedBanner_JP;
  //     case 'en-US':
  //     default:
  //       return Images.ArkWord_Church_Kickstarter_IntegratedBanner_EN;
  //   }
  // };

  const bibleBanner = Images.Facebook_Ad_WorldCupBible_2024_03;
  return (
    <div
      className={styles.learnMoreCardContainer}
      style={{ backgroundColor: !type ? 'transparent' : undefined }}
    >
      {(type === WebpageUI.InnovativeLearningMethod ||
        type === WebpageUI.BusinessLearningBible) && (
        <>
          <PromotionLearnMoreCard
            bannerSource={Images.JOYAJOY_IntegratedPhones_MemoryGameTypes}
            title={'JOYAJOY - Create Memory Practice'}
            link={'https://joyajoy.com/'}
            backgroundColor={'#DBF4F2'}
            titleColor={'#298D92'}
            buttonColor={'#00A698'}
          >
            <>
              <p style={{ color: '#1D656B', fontWeight: 'bold' }}>
                {t(
                  'Convert text to Memory Practice for learning faster and boosting memory retention up to 9 times.',
                )}
              </p>
              <p style={{ color: '#1D656B', fontWeight: 'bold' }}>
                {t(
                  'Use Cases: Dictation, Recitation, Lyrics, Scripts, Tests and Exams.',
                )}
              </p>
            </>
          </PromotionLearnMoreCard>

          <PromotionLearnMoreCard
            bannerSource={Images.ForWeb_FunEng_Competition_2024_Banner}
            title={'WEA World Cup (Online Weekly) • Fun English Spelling'}
            // link={'https://worldeducators.org/'}
            backgroundColor={'#DFF2FC'}
            titleColor={'#0099E6'}
            buttonColor={'#359AE3'}
          >
            <>
              <p style={{ color: '#1F5DA4', fontWeight: 'bold' }}>
                {t('Learn English Through Memory Practice')}
              </p>
              <p style={{ color: '#1F5DA4', fontWeight: 'bold' }}>
                {t('The World’s Largest Online Event for Learning English')}
              </p>
              <p className={styles.remainQuota}>
                {t('Worldwide Quota: ')}
                <span className={styles.quotaValue}>{'15,000,000'}</span>
              </p>
            </>
          </PromotionLearnMoreCard>
        </>
      )}

      {(type === WebpageUI.BusinessEnglishESG ||
        type === WebpageUI.BusinessLearningBible ||
        type === WebpageUI.WorldEducatorsAssociation) && (
        <>
          <PromotionLearnMoreCard
            bannerSource={Images.ForWeb_BusinessEng_Competition_2024_Banner}
            title={'WEA World Cup (Online Weekly) • Business English'}
            // link={'https://worldeducators.org/'}
            backgroundColor={'#DFF2FC'}
            titleColor={'#0099E6'}
            buttonColor={'#359AE3'}
          >
            <>
              <p style={{ color: '#1F5DA4', fontWeight: 'bold' }}>
                {t('Learn Business English Through Memory Practice')}
              </p>
              <p style={{ color: '#1F5DA4', fontWeight: 'bold' }}>
                {t('The World’s Largest Online Event for Business English')}
              </p>
              <p className={styles.remainQuota}>
                {t('Worldwide Quota: ')}
                <span className={styles.quotaValue}>{'3,000,000'}</span>
              </p>
            </>
          </PromotionLearnMoreCard>
          <PromotionLearnMoreCard
            bannerSource={Images.ForWeb_ESG_Competition_2024_Banner}
            title={'WEA World Cup (Online Weekly) • ESG (for General Staff)'}
            // link={'https://worldeducators.org/'}
            backgroundColor={'#DFF2FC'}
            titleColor={'#0099E6'}
            buttonColor={'#359AE3'}
          >
            <>
              <p style={{ color: '#1F5DA4', fontWeight: 'bold' }}>
                {t('Learn ESG Through Memory Practice')}
              </p>
              <p style={{ color: '#1F5DA4', fontWeight: 'bold' }}>
                {t('The World’s Largest Online Event for Learning ESG')}
              </p>
              <p className={styles.remainQuota}>
                {t('Worldwide Quota: ')}
                <span className={styles.quotaValue}>{'1,200,000'}</span>
              </p>
            </>
          </PromotionLearnMoreCard>
        </>
      )}

      {type === WebpageUI.WorldEducatorsAssociation && (
        <PromotionLearnMoreCard
          bannerSource={Images.ForWeb_FunEng_Competition_2024_Banner}
          title={'WEA World Cup (Online Weekly) • Fun English Spelling'}
          // link={'https://worldeducators.org/'}
          backgroundColor={'#DFF2FC'}
          titleColor={'#0099E6'}
          buttonColor={'#359AE3'}
        >
          <>
            <p style={{ color: '#1F5DA4', fontWeight: 'bold' }}>
              {t('Learn English Through Memory Practice')}
            </p>
            <p style={{ color: '#1F5DA4', fontWeight: 'bold' }}>
              {t('The World’s Largest Online Event for Learning English')}
            </p>
            <p className={styles.remainQuota}>
              {t('Worldwide Quota: ')}
              <span className={styles.quotaValue}>{'15,000,000'}</span>
            </p>
          </>
        </PromotionLearnMoreCard>
      )}

      {(type === WebpageUI.WorldCupBibleWeekly ||
        type === WebpageUI.BusinessLearningBible) && (
        <>
          <PromotionLearnMoreCard
            bannerSource={bibleBanner}
            title={'World Cup Bible • Weekly'}
            link={'https://arkword.org/'}
            backgroundColor={'#FEF3D7'}
            titleColor={'#BC6428'}
            buttonColor={'#EE8E38'}
          >
            <>
              <p style={{ color: '#762900', fontWeight: 'bold' }}>
                {t('Learn the Bible Through Memory Practice')}
              </p>
              <p style={{ color: '#762900', fontWeight: 'bold' }}>
                {t('The World’s Largest Online Event for Bible Learning')}
              </p>
              <p className={styles.remainQuota}>
                {t('Worldwide Quota: ')}
                <span className={styles.quotaValue}>{'2,500,000'}</span>
              </p>

              <hr
                style={{
                  width: '68%',
                  margin: '10px auto',
                }}
              />

              <p style={{ color: '#762900', fontWeight: 'bold' }}>
                {t('Languages for Selection')}
              </p>
              <BibleLanguagesGroup />
            </>
          </PromotionLearnMoreCard>
        </>
      )}

      <OpenDetails />
    </div>
  );
};

export const PromotionRewardsScreen = () => {
  const [searchParams] = useSearchParams();
  const { code: claimCode } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const code = claimCode || searchParams.get('code') || NOT_APPLICABLE_STRING;
  const { t, i18n } = useTranslation();

  const redemptionCode = useUserStore((store) => store.getRedemptionCode(code));

  const { setSharedData } = useShareData();

  const location = useLocation();
  useEffect(() => {
    setSharedData({
      pathName: location.pathname,
    });
  }, [location.pathname, setSharedData]);

  useEffect(() => {
    setIsLoading(true);
    fetchRedemptionCode(code ?? '')
      .then(() => {
        // Handle successful fetching if needed
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [code]);

  const { value: totalValue, desc } = getGiftCardValue(redemptionCode) ?? {};

  const promotionReward: PromotionReward = {
    title: 'VIP Gift Card', // isVIPGiftCard ? 'VIP Gift Card' : 'Gift Card',
    subtitle: redemptionCode?.name,
    rewards: {
      GameToken: redemptionCode?.reward.token,
      CreationToken: redemptionCode?.reward.creationToken,
      Diamond: redemptionCode?.reward.diamond,
      JCoin: redemptionCode?.reward.joyPoint,
    },
    remainingQuota:
      (redemptionCode?.quota ?? 0) - (redemptionCode?.redemptionCount ?? 0),
    totalQuota: redemptionCode?.quota ?? 0,
  };

  const rewardIcons: Record<keyof Rewards, PNGIconName> = {
    GameToken: 'GameToken',
    CreationToken: 'CreationToken',
    Diamond: 'Diamond',
    JCoin: 'JCoin',
  };

  const hasExpired = redemptionCode
    ? redemptionCode.expiredAt.getTime() <= Date.now()
    : null;

  const handleOpenApp = useDeepLink(`joyajoyapp://promotion?code=${code}`);

  // const handleClaim = () => {
  //   if (!claimCode) {
  //     const domain = window.location.origin;
  //     // universal link
  //     window.open(`${domain}/promotion/${code}/claim`, '_blank');
  //   } else {
  //     // fallback deep link
  //     handleOpenApp();
  //   }
  // };

  const getImageForLanguage = (lang: UiLocale) => {
    switch (lang) {
      case UiLocale.CN:
        return Images.JOYAJOY_logo_Text_Slogan_SC;
      case UiLocale.HK:
      case UiLocale.TW:
        return Images.JOYAJOY_logo_Text_Slogan_TC;
      case UiLocale.JP:
        return Images.JOYAJOY_logo_Text_Slogan_JP;
      case UiLocale.US:
      default:
        return Images.JOYAJOY_logo_Text_Slogan_EN;
    }
  };

  const logoImage = getImageForLanguage(
    getUiLocale(i18n.language as TranslationLocale),
  );

  const learnMoreType = redemptionCode?.webpageUi;

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.logoInfoContainer}>
          <img
            className={styles.joyajoyIcon}
            src={Icons[IconType.JoyajoyIcon]}
          />
          <img className={styles.joyajoyLogo} src={logoImage} />
          <p className={styles.logoTitle}>
            {t('The Best Social & Learning App')}
          </p>
        </div>

        <hr />

        {redemptionCode ? (
          <>
            <p className={styles.title}>{t(promotionReward.title)}</p>

            {promotionReward.subtitle ? (
              <p className={styles.subtitle}>{promotionReward.subtitle}</p>
            ) : null}

            <div className={styles.rewardsContainer}>
              {/* <p className={styles.rewardTitle}>{t('Rewards')}</p> */}
              {Object.entries(promotionReward.rewards)
                .filter(([, value]) => value != undefined)
                .sort(([, value1], [, value2]) => (value1 > value2 ? -1 : 1))
                .map(([key, value]) => (
                  <div className={styles.rewardItem} key={key}>
                    <PngIcon
                      name={rewardIcons[key as keyof Rewards]}
                      width={32}
                      height={32}
                    />
                    <p className={styles.text}>
                      {`${t(`${key}: `)}`}
                      <span
                        className={styles.rewardAmountValue}
                      >{`${value}`}</span>
                    </p>
                  </div>
                ))}

              {totalValue ? (
                <>
                  <hr className={styles.divider} />
                  <p className={styles.text}>
                    {t('Total Value: ')}
                    <span className={styles.rewardAmountValue}>
                      {totalValue}
                    </span>
                  </p>
                </>
              ) : null}
            </div>

            <p className={styles.codeLabel}>
              {t('Code: ')}
              <span className={styles.codeValue}>{code}</span>
            </p>
            {desc ? <p className={styles.caption}>{t(desc)}</p> : null}

            <hr className={styles.divider} />

            {/* <p className={styles.steps}>{t('Steps to claim rewards: ')}</p> */}
            <div className={styles.stepsList}>
              <p className={styles.stepInfo}>{t('Step 1: Download JOYAJOY')}</p>
              {/* <button
                className={styles.button}
                onClick={() => window.open(DEFAULT_APP_STORE_URL, '_blank')}
              >
                {t('Download')}
                <SvgIcon
                  name={'Arrow'}
                  className={styles.arrowIcon}
                  width={20}
                  height={20}
                  color={'#ffffff'}
                />
              </button> */}
              <a
                className={styles.appStoreButton}
                href={DEFAULT_APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={Images.DownloadFromAppStore}
                  alt="Download From AppStore"
                  style={{ height: '100%', width: 'auto' }}
                />
              </a>
              <p className={styles.stepInfo}>
                {t('Step 2: Click "Claim" button')}
              </p>
              <button className={styles.button} onClick={handleOpenApp}>
                {t('Claim')}
                <SvgIcon
                  name={SVGIconType.Arrow}
                  className={styles.arrowIcon}
                  width={20}
                  height={20}
                  color={'#ffffff'}
                />
              </button>
              {hasExpired === true ? (
                <p className={styles.countdown}>{t('Expired')}</p>
              ) : hasExpired === false ? (
                <CountDownText
                  timestamp={redemptionCode.expiredAt.getTime()}
                  label={t('Expiry: ')}
                  className={styles.countdown}
                />
              ) : null}
            </div>

            {redemptionCode?.quota ? (
              <div className={styles.quotaContainer}>
                <p className={styles.remainQuota}>
                  {t('Total Quota: ')}{' '}
                  <span className={styles.quotaValue}>
                    {promotionReward.totalQuota?.toLocaleString()}
                  </span>
                </p>
                <p className={styles.remainQuota}>
                  {t('Remaining Quota:')}{' '}
                  <span className={styles.quotaValue}>
                    {promotionReward.remainingQuota?.toLocaleString()}
                  </span>
                </p>
              </div>
            ) : null}
            {/* : (
              // <p className={styles.callout}>{t('Early Bird Reward')}</p>
            )} */}

            <div className={styles.alternativeMethods}>
              <p className={styles.disclaimer}>
                {t(
                  'If the "Claim" button does not work in the current app, please click the button below to copy link and paste it in an external browser.',
                )}
              </p>
              <ShareUrl />
              <p className={styles.disclaimer}>
                {t(
                  'Scanning the following QR Code can also open link in an external browser.',
                )}
              </p>
              <QRCodeSVG
                value={window.location.href}
                size={220}
                className={styles.qrcode}
              />
            </div>

            <LearnMoreCardGroup type={learnMoreType} />
          </>
        ) : isLoading ? (
          <LoadingSpinner loading={isLoading} />
        ) : (
          <p className={styles.text}>{t('Expired or Invalid Code')}</p>
        )}
      </div>
    </div>
  );
};

interface Rewards {
  GameToken?: number;
  CreationToken?: number;
  Diamond?: number;
  JCoin?: number;
}

interface PromotionReward {
  title: string;
  subtitle?: string | null;
  rewards: Rewards;
  remainingQuota?: number;
  totalQuota?: number;
}
